// DiscordNotificationsCard — owner control over WHAT gets posted to Discord
// (migration 0054). Per-type toggles the poster daemon reads each tick, plus a
// "Send test notification" button that writes discord_test_requested_at so the
// poster fires one test card (proves alerts work). The master on/off lives on
// the Discord tile in ConnectedServicesCard (app_settings.discord_bot_enabled);
// this card is the granularity under it.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckSquare, DollarSign, Loader2, Mail, Send, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import GlassCard from '@/components/shared/GlassCard';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/lib/supabase';

type NotifyKey =
  | 'discord_notify_email'
  | 'discord_notify_focus'
  | 'discord_notify_commission'
  | 'discord_notify_digest'
  | 'discord_notify_urgent_mention';

const NOTIFY_ROWS: { key: NotifyKey; icon: typeof Mail; label: string; hint: string }[] = [
  {
    key: 'discord_notify_email',
    icon: Mail,
    label: 'Email drafts',
    hint: 'when a voice-matched reply is ready to review',
  },
  {
    key: 'discord_notify_focus',
    icon: CheckSquare,
    label: 'Focus reschedules',
    hint: 'when an event overlaps a focus block',
  },
  {
    key: 'discord_notify_commission',
    icon: DollarSign,
    label: 'Commission exports',
    hint: 'when a payroll export is ready to approve',
  },
  {
    key: 'discord_notify_digest',
    icon: Sparkles,
    label: 'Daily digest',
    hint: 'the "where your time went" report each morning',
  },
  {
    key: 'discord_notify_urgent_mention',
    icon: Bell,
    label: '@-mention me on urgent',
    hint: 'ping your phone on urgent items only',
  },
];

const ALL_KEYS = NOTIFY_ROWS.map((r) => r.key);

async function fetchNotifyPrefs(): Promise<Record<string, boolean>> {
  const { data, error } = await supabase
    .from('app_settings')
    .select('key, value')
    .in('key', ALL_KEYS);
  if (error) throw error;
  const map: Record<string, boolean> = {};
  for (const k of ALL_KEYS) map[k] = true; // seeded true; default on if a row is momentarily absent
  for (const row of (data as { key: string; value: string }[]) ?? [])
    map[row.key] = row.value === 'true';
  return map;
}

async function writeNotifyPref(key: NotifyKey, next: boolean): Promise<void> {
  const { data, error } = await supabase
    .from('app_settings')
    .update({ value: next ? 'true' : 'false' })
    .eq('key', key)
    .select('key');
  if (error) throw error;
  if (!data || data.length === 0) throw new Error(`Notify write affected zero rows for ${key}`);
}

async function requestTestNotification(): Promise<void> {
  const { data, error } = await supabase
    .from('app_settings')
    .update({ value: new Date().toISOString() })
    .eq('key', 'discord_test_requested_at')
    .select('key');
  if (error) throw error;
  if (!data || data.length === 0) throw new Error('Test request affected zero rows');
}

export default function DiscordNotificationsCard(): JSX.Element {
  const qc = useQueryClient();
  const prefsQ = useQuery({
    queryKey: ['discord_notify_prefs'],
    queryFn: fetchNotifyPrefs,
    refetchInterval: 15_000,
  });

  const toggle = useMutation({
    mutationFn: ({ key, next }: { key: NotifyKey; next: boolean }) => writeNotifyPref(key, next),
    onMutate: async ({ key, next }) => {
      await qc.cancelQueries({ queryKey: ['discord_notify_prefs'] });
      const prev = qc.getQueryData<Record<string, boolean>>(['discord_notify_prefs']);
      qc.setQueryData<Record<string, boolean>>(['discord_notify_prefs'], (old) => ({
        ...(old ?? {}),
        [key]: next,
      }));
      return { prev };
    },
    onError: (err: Error, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['discord_notify_prefs'], ctx.prev);
      toast.error(err.message);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['discord_notify_prefs'] }),
  });

  const test = useMutation({
    mutationFn: requestTestNotification,
    onSuccess: () => toast.success('Test sent — watch your Discord (~10 seconds).'),
    onError: (err: Error) => toast.error(err.message),
  });

  const prefs = prefsQ.data;

  return (
    <GlassCard hover={false}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">Discord notifications</p>
          <p className="mt-0.5 text-xs text-muted-foreground max-w-md">
            Choose what posts to your Discord server. Turn Discord off entirely from the Discord
            tile above.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => test.mutate()}
          disabled={test.isPending}
        >
          {test.isPending ? (
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          ) : (
            <Send className="w-3.5 h-3.5 mr-1.5" />
          )}
          Send test notification
        </Button>
      </div>

      {prefsQ.isError ? (
        <p className="mt-3 text-xs text-alert">
          Couldn&apos;t load notification settings: {(prefsQ.error as Error).message}
        </p>
      ) : (
        <div className="mt-4 space-y-1">
          {NOTIFY_ROWS.map((row) => {
            const Icon = row.icon;
            const on = prefs?.[row.key] ?? true;
            return (
              <div
                key={row.key}
                className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-white/50 transition-colors"
              >
                <span className="neo-chip w-8 h-8 flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-forest/70" aria-hidden />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] text-foreground leading-tight">{row.label}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{row.hint}</p>
                </div>
                <Switch
                  checked={on}
                  disabled={!prefs || toggle.isPending}
                  onCheckedChange={(next) => toggle.mutate({ key: row.key, next })}
                  aria-label={row.label}
                />
              </div>
            );
          })}
        </div>
      )}
    </GlassCard>
  );
}
